/**
 * "Send this note to someone" (0048).
 *
 * Project notes stopped appearing on the client portal — they are the
 * designer's own working notes, and the portal published every one of them,
 * including any note ABOUT the client. So a note now reaches a person because
 * the designer chose to send it.
 *
 * Same shape as `punch-message.ts`: pick a recipient, read the message, send
 * through the device's own share sheet, record it in Notifications afterwards
 * so it can be looked at and sent again.
 *
 * ⚠️ THE GESTURE TRAP (see share-sheet.ts): navigator.share() must be called
 * synchronously inside the click handler, so the message text is composed at
 * render time and rebuilt whenever the recipient changes — never fetched. The
 * recording, which IS a network call, happens after the share resolves.
 */

import { esc } from '@/lib/dom';
import { shareOrFallback, countsAsShared } from '@/lib/share-sheet';
import { recordSentMessage } from '@/lib/data/notifications';
import { noteMessage, noteShareTitle, noteRecipients, recipientLabel, greetingFor } from '@/lib/note-message';
import { companyName } from '@/lib/branding';
import { currentFirm } from '@/lib/current-firm';
import { exportBranding } from '@/lib/export';
import { toastError } from '@/lib/toast';
import type { NoteRow, DesignerContactRow, ProjectRow } from '@/types/database';

export interface NoteSendOptions {
  project: Pick<ProjectRow, 'id' | 'name' | 'client_name'>;
  note: NoteRow;
  contacts: DesignerContactRow[];
  onSent: (toName: string) => void;
}

export function openNoteSend(opts: NoteSendOptions): HTMLElement {
  const { project, note, contacts } = opts;
  const people = noteRecipients(contacts);
  // '' is a real choice: "nobody in particular", where the share sheet asks.
  let contactId = people[0]?.id ?? '';

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  const chosen = (): DesignerContactRow | null => contacts.find((c) => c.id === contactId) ?? null;
  const messageFor = (): string => noteMessage({
    note,
    projectName: project.name,
    toName: greetingFor(chosen(), project),
    fromName: companyName(exportBranding(), currentFirm()?.name),
  });

  const draw = (): void => {
    overlay.innerHTML = `
      <div class="modal-card punch-message" role="dialog" aria-label="Send this note">
        <h2>Send this note</h2>
        <p class="muted small">Notes aren’t shown on the client’s link — they’re yours. Sending one
           is how a client or a contractor sees it.</p>

        <label class="field">
          <span>Send to</span>
          <select data-note-to>
            ${people.map((c) => `<option value="${esc(c.id)}" ${c.id === contactId ? 'selected' : ''}>${
              esc(recipientLabel(c))}</option>`).join('')}
            <option value="" ${contactId === '' ? 'selected' : ''}>Someone else…</option>
          </select>
        </label>

        <div class="field">
          <span>Message</span>
          <pre class="punch-message-preview" data-note-preview>${esc(messageFor())}</pre>
        </div>
        ${chosen()?.phone
          ? ''
          : `<p class="muted small">No phone on file for them — your share sheet will ask who to send to.</p>`}

        <div class="modal-actions">
          <span style="flex:1"></span>
          <button type="button" class="btn-link" data-do="close">Cancel</button>
          <button type="button" class="btn-primary" data-do="send">Send</button>
        </div>
      </div>`;
    wire();
  };

  function wire(): void {
    overlay.querySelectorAll<HTMLSelectElement>('[data-note-to]').forEach((sel) => {
      // Redraw rather than patch the preview: a stale preview beside a Send
      // button is the one thing that must not happen (punch-message.ts's rule).
      sel.addEventListener('change', () => { contactId = sel.value; draw(); });
    });
    overlay.querySelectorAll<HTMLButtonElement>('[data-do="close"]').forEach((b) => b.addEventListener('click', close));
    overlay.querySelectorAll<HTMLButtonElement>('[data-do="send"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const to = chosen();
        const text = messageFor();
        // No await before this call — see the header.
        const outcome = await shareOrFallback({
          title: noteShareTitle(note, project.name), text, phone: to?.phone,
        });
        if (!countsAsShared(outcome)) {
          if (outcome === 'copied') toastError('Copied the note — paste it into your messaging app.');
          else if (outcome === 'failed') toastError('Couldn’t open a way to send that note.');
          return;   // dismissed: they changed their mind, record nothing
        }
        try {
          await recordSentMessage({
            projectId: project.id, contactId: to?.id ?? null, kind: 'note', text, phone: to?.phone ?? null,
          });
        } catch {
          // The note has already gone. A failed record is worth less than an
          // error dialog claiming the send failed.
        }
        close();
        opts.onSent(to?.name ?? 'them');
      });
    });
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  draw();
  document.body.append(overlay);
  document.addEventListener('keydown', onKey);
  return overlay;
}
